import React from 'react';
import {Alert, Button, Container} from 'react-bootstrap';
import {useNavigate} from 'react-router-dom';
import axios from 'axios';
import {useAuth} from '../auth/auth-context';
import {track} from '../libs/analytics';
import TrustBar from '../components/trust-bar';

export const priceMonth = 20.99;
export const priceBiannual = 110.99;
export const priceYear = 179.99;
export const freeTrialActive = true;
export const freeTrialDays = 7;

export const pricingPriceIds: Record<string, string> =
  process.env.NODE_ENV === 'production'
    ? {
        // Live price ids
        monthly: 'price_1SaYPkEDCZHE2VHmqDuoDQLG',
        biannual: 'price_1SaYQGEDCZHE2VHm6c9uvLMJ',
        yearly: 'price_1SaYRgEDCZHE2VHmGlYwHovC',
      }
    : {
        // Sandbox price ids
        monthly: 'price_1SZkJrCz8tGWEKq35dJy2Q34',
        biannual: 'price_1Sa6wFCz8tGWEKq3lqOQxoHy',
        yearly: 'price_1Sa6vvCz8tGWEKq3MQO1oBPg',
      };

const defaultRedirectSeconds = 10;

type BillingCheckoutSessionOptions = {
  priceId?: string;
  planKey?: string;
  successUrl?: string;
  cancelUrl?: string;
};

type BillingCheckoutSessionArg =
  | BillingCheckoutSessionOptions
  | React.SyntheticEvent
  | undefined;

type BillingPortalSessionArg = string | React.SyntheticEvent | undefined;

function useHomeRedirectTimer(initialSeconds: number = defaultRedirectSeconds) {
  const navigate = useNavigate();
  const [seconds, setSeconds] = React.useState(initialSeconds);

  React.useEffect(() => {
    if (seconds <= 0) {
      void navigate('/', {replace: true});
    }

    const id = globalThis.setInterval(() => {
      setSeconds(s => s - 1);
    }, 1000);

    return () => {
      globalThis.clearInterval(id);
    };
  }, [seconds, navigate]);

  return {seconds, navigate};
}

/**
 * Create a Stripe Checkout Session via the backend and navigate to the returned checkout URL.
 * This function is usable both as a direct caller (createBillingCheckoutSession({ priceId: '...' }))
 * and as a React onClick handler (<Button onClick={createBillingCheckoutSession}>).
 * We detect if the first argument is a MouseEvent and treat it as a no-opts call in that case.
 */
export async function createBillingCheckoutSession(
  arg?: BillingCheckoutSessionArg,
): Promise<void> {
  const options =
    arg && typeof arg === 'object' && 'preventDefault' in arg ? undefined : arg;

  const successUrl =
    options?.successUrl ?? `${globalThis.location.origin}/checkout/success`;
  const cancelUrl =
    options?.cancelUrl ?? `${globalThis.location.origin}/checkout/cancelled`;

  try {
    track('checkout_started', {
      priceId: options?.priceId ?? null,
      planKey: options?.planKey ?? null,
    });

    const response = await axios.post<{checkout_url?: string}>(
      '/api/billing/checkout_session',
      null,
      {
        params: {
          price_id: options?.priceId ?? null, // eslint-disable-line @typescript-eslint/naming-convention
          success_url: successUrl, // eslint-disable-line @typescript-eslint/naming-convention
          cancel_url: cancelUrl, // eslint-disable-line @typescript-eslint/naming-convention
        },
        // Don't include credentials by default; axios global settings will apply if configured
      },
    );

    const url = response?.data?.checkout_url;
    if (!url) {
      throw new Error('Backend did not return a checkout URL');
    }

    // Navigate to Stripe Checkout (full page navigation)
    globalThis.location.assign(url);
  } catch (error) {
    console.error('Failed to create checkout session', error);
    throw error;
  }
}

/**
 * Create a Stripe Billing Portal Session via the backend and navigate to the returned portal URL.
 * This function is usable both as a direct caller (createBillingPortalSession('/return')) and as a
 * React onClick handler (<Button onClick={createBillingPortalSession}>). If called as an event handler
 * the event is detected and a null return_url is sent to the backend.
 */
export async function createBillingPortalSession(
  arg?: BillingPortalSessionArg,
): Promise<void> {
  const returnUrl = typeof arg === 'string' ? arg : undefined;

  try {
    track('portal_opened');
    const response = await axios.post<{portal_url?: string}>(
      '/api/billing/portal_session',
      null,
      {
        params: {return_url: returnUrl ?? null}, // eslint-disable-line @typescript-eslint/naming-convention
      },
    );

    const url = response?.data?.portal_url;
    if (!url) {
      throw new Error('Backend did not return a portal URL');
    }

    globalThis.location.assign(url);
  } catch (error) {
    console.error('Failed to create portal session', error);
    throw error;
  }
}

export function PricingPage() {
  const auth = useAuth();
  const navigate = useNavigate();

  const handlePrimaryCta = async () => {
    // If user is signed in, open billing portal.
    if (auth.isAuthenticated) {
      try {
        await createBillingPortalSession();
      } catch (error) {
        console.error('Failed to open portal', error);
      }

      return;
    }

    // Not signed in -> navigate to signup page so users complete account creation before starting free trial.
    try {
      await navigate('/signup');
    } catch (error) {
      console.error('Failed to navigate to signup', error);
      // Fallback to a full page redirect
      globalThis.location.assign('/signup');
    }
  };

  const primaryCtaLabel = auth.isAuthenticated
    ? 'Manage subscription'
    : `Start ${freeTrialDays}-day free trial`;

  return (
    <Container className='my-5'>
      <PricingHero
        primaryCtaLabel={primaryCtaLabel}
        onPrimaryCta={() => {
          void handlePrimaryCta();
        }}
        secondaryCta={undefined}
      />

      <div className='my-4'>
        <TrustBar />
      </div>

      <h2 className='mt-4'>Plans & Pricing</h2>
      <p className='text-muted'>
        Pick a plan that fits — upgrade or cancel anytime.
      </p>

      <SubscriptionPricingTable showTrial={true} />
    </Container>
  );
}

export function SubscriptionPricingTable(props?: {showTrial?: boolean}) {
  const {showTrial} = props ?? {};
  const {banned, disabled, activated, isAuthenticated} = useAuth();

  let displayMode: 'readonly' | 'subscribe' | 'signup' = 'readonly';
  if (!isAuthenticated) {
    displayMode = 'signup';
  } else if (banned) {
    displayMode = 'readonly';
  } else if (!activated) {
    displayMode = 'subscribe';
  } else if (disabled) {
    displayMode = 'subscribe';
  } else if (isAuthenticated) {
    displayMode = 'readonly';
  }

  const shouldDisplayTrial: boolean =
    typeof showTrial === 'boolean'
      ? showTrial && freeTrialActive
      : freeTrialActive;

  return (
    <SubscriptionPricingTableInternal
      mode={displayMode}
      showTrial={shouldDisplayTrial}
    />
  );
}

/**
 * SubscriptionPricingTable
 *
 * Props:
 *  - mode: 'readonly' | 'subscribe' | 'signup';
 *
 * Behavior:
 *  - When mode === 'readonly' the buttons are disabled (read-only view)
 *  - When mode === 'subscribe' the subscribe flow is enabled
 *  - When mode === 'signup' buttons will navigate to the signup page
 */
function SubscriptionPricingTableInternal(props?: {
  mode?: 'readonly' | 'subscribe' | 'signup';
  showTrial?: boolean;
}) {
  const {mode, showTrial} = props ?? {};
  const navigate = useNavigate();
  const auth = useAuth();

  const isReadOnly = mode === 'readonly';
  const allowSubscribe = mode === 'subscribe';
  const allowSignup = mode === undefined || mode === 'signup';

  const showTrialFlag =
    typeof showTrial === 'boolean' ? showTrial : freeTrialActive;

  const handleSubscribe = async (planKey?: string) => {
    try {
      const selectedPriceId = planKey
        ? (pricingPriceIds[planKey] ?? undefined)
        : undefined;
      await createBillingCheckoutSession({
        priceId: selectedPriceId,
        planKey,
        successUrl: `${globalThis.location.origin}/checkout/success`,
        cancelUrl: `${globalThis.location.origin}/checkout/cancelled`,
      });
    } catch (error) {
      console.error('Failed to start checkout', error);
    }
  };

  const renderAction = (planKey: string, variant?: string) => {
    // If user is authenticated but the current display mode allows subscribing (e.g. account is disabled
    // and we want to let the user re-subscribe), show the Subscribe button. Otherwise, authenticated
    // users should see "Manage subscription" which opens the billing portal.
    if (auth && auth.isAuthenticated && !allowSubscribe) {
      return (
        <Button
          onClick={() => {
            void createBillingPortalSession();
          }}
          variant={variant ?? 'outline-primary'}
          className='w-100'
        >
          Manage subscription
        </Button>
      );
    }

    if (allowSubscribe) {
      return (
        <Button
          onClick={() => {
            void handleSubscribe(planKey);
          }}
          variant={variant}
          className='w-100'
          disabled={isReadOnly}
        >
          Subscribe
        </Button>
      );
    }

    if (allowSignup) {
      return (
        <Button
          onClick={() => {
            void navigate('/signup');
          }}
          variant={variant}
          className='w-100 btn-signup'
          disabled={isReadOnly}
        >
          Sign Up
        </Button>
      );
    }

    return (
      <Button disabled className='w-100'>
        Subscribe
      </Button>
    );
  };

  return (
    <div>
      {showTrialFlag ? (
        <Alert variant='info' className='mb-3'>
          *NOTE* All subscriptions come with a free {freeTrialDays}-day trial.
        </Alert>
      ) : null}
      <div className='row'>
        <div className='col-md-4 mb-3'>
          <div className='card h-100'>
            <div className='card-body d-flex flex-column'>
              <h5 className='card-title'>Monthly</h5>
              <h6 className='card-subtitle mb-2 text-muted'>{`$${priceMonth.toFixed(2)} / month`}</h6>
              <p className='card-text'>Billed monthly. Cancel anytime.</p>
              <ul>
                <li>Access to premium features</li>
                <li>Priority support</li>
              </ul>
              <div className='mt-auto'>{renderAction('monthly')}</div>
            </div>
          </div>
        </div>

        <div className='col-md-4 mb-3'>
          <div className='card h-100'>
            <div className='card-body d-flex flex-column'>
              <h5 className='card-title'>Bi-Annual</h5>
              <h6 className='card-subtitle mb-2 text-muted'>{`$${priceBiannual.toFixed(2)} (every 6 months) — $${(priceBiannual / 6).toFixed(2)} / month`}</h6>
              <p className='card-text'>Save by paying every six months.</p>
              <ul>
                <li>All Monthly benefits</li>
                <li>Better value than monthly</li>
              </ul>
              <div className='mt-auto'>
                {renderAction('biannual', 'primary')}
              </div>
            </div>
          </div>
        </div>

        <div className='col-md-4 mb-3'>
          <div className='card h-100'>
            <div className='card-body d-flex flex-column'>
              <h5 className='card-title'>Yearly</h5>
              <h6 className='card-subtitle mb-2 text-muted'>{`$${priceYear.toFixed(2)} / year — $${(priceYear / 12).toFixed(2)} / month`}</h6>
              <p className='card-text'>Best value — billed annually.</p>
              <ul>
                <li>All Bi-Annual benefits</li>
                <li>Lowest effective monthly price</li>
              </ul>
              <div className='mt-auto'>{renderAction('yearly', 'success')}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PricingHero(props?: {
  primaryCtaLabel?: string;
  onPrimaryCta?: () => void;
  secondaryCta?: {label: string; onClick: () => void};
}) {
  const {
    primaryCtaLabel = `Start ${freeTrialDays}-day free trial`,
    onPrimaryCta,
    secondaryCta,
  } = props ?? {};
  const yearlySavingsPct = Math.round(
    100 * (1 - priceYear / (priceMonth * 12)),
  );

  const handlePrimary = React.useCallback(() => {
    try {
      track('pricing_hero_primary_cta');
    } catch {}

    onPrimaryCta?.();
  }, [onPrimaryCta]);

  const isFreeTrialCta = /\bfree trial\b/i.test(primaryCtaLabel);

  return (
    <div className='bg-light p-4 rounded'>
      <div className='d-flex flex-column flex-md-row align-items-start'>
        <div className='flex-grow-1 pe-md-4'>
          <h1 className='mb-2'>Stop rewriting. Start interviewing.</h1>
          <p className='lead text-muted mb-3'>
            Turn one resume into many job‑ready versions in minutes.
          </p>
          <ul className='mb-3 small'>
            <li>
              <strong>Save time:</strong> tailor with AI instead of rewriting
              from scratch
            </li>
            <li>
              <strong>Get more interviews:</strong> keyword‑boosted, quantified
              bullets
            </li>
            <li>
              <strong>Take charge:</strong> ATS‑optimized templates and fast PDF
              export
            </li>
          </ul>
          <div className='d-flex flex-wrap gap-2 mb-2'>
            <div className={isFreeTrialCta ? 'cta-callout' : undefined}>
              <Button
                size='lg'
                className={`me-2 ${isFreeTrialCta ? 'btn-cta' : ''}`}
                variant='primary'
                onClick={handlePrimary}
              >
                {primaryCtaLabel}{' '}
                {isFreeTrialCta ? (
                  <span className='cta-callout__arrow' aria-hidden>
                    →
                  </span>
                ) : null}
              </Button>
            </div>
            {secondaryCta ? (
              <Button
                size='lg'
                variant='outline-primary'
                onClick={secondaryCta.onClick}
              >
                {secondaryCta.label}
              </Button>
            ) : null}
          </div>
        </div>

        <div className='mt-3 mt-md-0 ms-md-4' style={{minWidth: 260}}>
          <div className='card border-0 shadow-sm'>
            <div className='card-body'>
              <div className='mb-2'>
                <small className='text-muted'>Best value</small>
                <div className='d-flex justify-content-between align-items-baseline'>
                  <h3 className='mb-0'>${priceYear.toFixed(2)}</h3>
                  <div className='badge bg-success'>
                    {yearlySavingsPct}% savings
                  </div>
                </div>
                <small className='text-muted'>
                  per year (${(priceYear / 12).toFixed(2)}/mo avg)
                </small>
              </div>
              <hr />
              <div className='small text-muted'>
                <div>
                  <strong>Monthly:</strong> ${priceMonth.toFixed(2)}/mo
                </div>
                <div>
                  <strong>Yearly:</strong> ${priceYear.toFixed(2)} —{' '}
                  {yearlySavingsPct}% savings
                </div>
              </div>
              <hr />
              <div className='small'>
                <div
                  className='d-flex align-items-center mb-1'
                  aria-live='polite'
                >
                  <span aria-hidden>✓</span>
                  <span className='ms-2'>Unlimited AI rewrites</span>
                </div>
                <div className='d-flex align-items-center'>
                  <span aria-hidden>✓</span>
                  <span className='ms-2'>Priority support</span>
                </div>
                <div className='d-flex align-items-center'>
                  <span aria-hidden>✓</span>
                  <span className='ms-2'>Secure Stripe Checkout</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Payment cancelled page with 10s redirect timer
export function CheckoutCancelledPage() {
  const {seconds, navigate} = useHomeRedirectTimer();
  React.useEffect(() => {
    track('checkout_cancelled');
  }, []);
  return (
    <Container className='my-5'>
      <Alert variant='danger'>
        Payment cancelled. Redirecting to the home page in {seconds} second
        {seconds === 1 ? '' : 's'}.
        <div className='mt-3'>
          <Button
            variant='outline-primary'
            onClick={() => {
              void navigate('/', {replace: true});
            }}
          >
            Go to home now.
          </Button>
        </div>
      </Alert>
    </Container>
  );
}

// Payment succeeded page with 10s redirect timer
export function CheckoutSucceededPage() {
  const {seconds, navigate} = useHomeRedirectTimer();
  React.useEffect(() => {
    (async () => {
      try {
        type BillingStatus = {
          active: boolean;
          status: string;
          trial_end?: string | undefined;
          current_period_end?: string | undefined;
          cancel_at_period_end?: boolean;
          customer_id?: string | undefined;
          subscription_id?: string | undefined;
        };
        const response = await axios.get<BillingStatus>('/api/billing/status');
        const data = response?.data as Partial<BillingStatus> | undefined;
        if (data?.active === true) {
          track('subscription_activated', data);
        } else {
          track('checkout_success_page_no_active', data ?? {});
        }
      } catch {
        track('checkout_success_status_error');
      }
    })();
  }, []);
  return (
    <Container className='my-5'>
      <Alert variant='success'>
        Payment succeeded. Redirecting to the home page in {seconds} second
        {seconds === 1 ? '' : 's'}.
        <div className='mt-3'>
          <Button
            variant='outline-primary'
            onClick={() => {
              void navigate('/', {replace: true});
            }}
          >
            Go to home now.
          </Button>
        </div>
      </Alert>
    </Container>
  );
}
